/**
 * StageBar — the deal page's stage bar (deal detail slice 1, §39.9; spec
 * §3.3): five columns, a 6px bar each, the current stage in the accent with
 * its time in stage, past stages in the ramp's lightest step with the time
 * they took (from the existing stage-events read), future stages in the
 * control fill. The primary button on the right moves the deal one step
 * ("Move to Qualified"); in Negotiation it becomes "Mark won", which opens
 * the close ceremony — the server refuses closed stages on the stage route.
 *
 * Slice 6 (R6): the current stage's time reads red (danger) when it is more
 * than `thresholdDays` — 2 × the stage's won-deal median, or 21 days without
 * one (the server's assessment.stage.thresholdDays, §40.12). A colour only, no notification
 * (§36.1 d). Null = nothing red (the medians read still in flight, or a closed
 * deal). Replaces the pill row, the plain "Nd in stage" chip and, since slice
 * 6, the server health chips beside it.
 */
import type { DealStage } from '../../types'
import type { SalesDealStageEvent } from '../../../../api/types'
import { accent, danger, dealStageBar } from '../../../../theme/tokens'
import { STAGE_STEPS, stepState, nextOpenStage, daysInStep, daysLabel } from './stageBar'
import { useWriteAccess } from '../../../../lib/writeAccess'

export function StageBar({ stage, daysInStage, events, now, moving, thresholdDays = null, onMove, onMarkWon }: {
  stage: DealStage
  /** Days in the CURRENT stage: London calendar days from stageChangedAt to
   *  the server's today (slice 6). */
  daysInStage: number
  /** The stage-events read; null while loading or failed → past steps show no time. */
  events: readonly SalesDealStageEvent[] | null
  now: number
  moving: boolean
  /** R6: red when daysInStage exceeds it. Null = nothing red. */
  thresholdDays?: number | null
  onMove: (to: DealStage) => void
  onMarkWon: () => void
}) {
  const next = nextOpenStage(stage)
  // Stage moves and the close are records-level writes (lib/writeAccess).
  const { records } = useWriteAccess()
  const closed = stage === 'Closed Won' || stage === 'Closed Lost'
  const overdue = thresholdDays != null && daysInStage > thresholdDays
  return (
    // .k-card is a COLUMN flex box (klaxiora.css), so the row lives on an inner
    // div: the grid fills the left, the button holds the right.
    <div className="k-card px-5 py-4" data-stage-bar data-stage={stage} data-red-after={thresholdDays ?? ''}>
     <div className="flex items-center gap-5">
      <div className="grid flex-1 min-w-0 gap-3" style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }} role="list" aria-label="Stages">
        {STAGE_STEPS.map(step => {
          const st = stepState(step, stage)
          const days = st === 'current' ? daysInStage : st === 'past' && events ? daysInStep(events, step, now) : null
          const fill = st === 'current' ? dealStageBar.current : st === 'past' ? dealStageBar.past : dealStageBar.future
          return (
            <div key={step} role="listitem" data-step={step} data-state={st} className="min-w-0" aria-current={st === 'current' ? 'step' : undefined}>
              <div className="h-1.5 rounded-[3px]" style={{ background: fill }} aria-hidden="true" />
              <div className={`mt-2 text-13 truncate ${st === 'current' ? 'font-semibold text-ink-primary' : st === 'past' ? 'text-ink-secondary' : 'text-ink-muted'}`}>
                {step}
                {days != null && (
                  <span className="font-normal" style={st === 'current' && overdue ? { color: danger.text } : undefined}
                    data-days={days}> · {daysLabel(days)}</span>
                )}
              </div>
            </div>
          )
        })}
      </div>
      {stage === 'Closed Lost' && (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-12 font-medium" style={{ background: danger.bg, color: danger.text }}>Closed Lost</span>
      )}
      {!closed && records && (next ? (
        <button type="button" className="k-btn k-btn--lg disabled:opacity-60 flex-shrink-0 whitespace-nowrap" disabled={moving} onClick={() => onMove(next)}
          style={{ background: accent.DEFAULT }}>
          {moving ? 'Moving…' : `Move to ${next}`}
        </button>
      ) : stage === 'Negotiation' ? (
        <button type="button" className="k-btn k-btn--lg disabled:opacity-60 flex-shrink-0 whitespace-nowrap" disabled={moving} onClick={onMarkWon}>Mark won</button>
      ) : null)}
     </div>
    </div>
  )
}
